import React, { useState } from 'react';

// ── Terms & Conditions content ────────────────────────────────────────────────
// Update the sections below with your actual legal text before going live.
const TERMS_SECTIONS = [
  {
    title: '1. Agreement to Terms',
    body: `By accessing or using ClockEarnings ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these Terms, you may not access the Service. These Terms apply to all users, including organization administrators and employees.`,
  },
  {
    title: '2. Description of Service',
    body: `ClockEarnings provides time-tracking, payroll management, scheduling, and related workforce management tools to businesses and their employees. Features available to your organization depend on your selected subscription plan.`,
  },
  {
    title: '3. Account Registration & Responsibilities',
    body: `You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate, current, and complete information during registration and to update such information as necessary. You must notify us immediately of any unauthorized use of your account. ClockEarnings is not liable for any loss resulting from unauthorized account access.`,
  },
  {
    title: '4. Acceptable Use',
    body: `You agree to use the Service only for lawful purposes and in a way that does not infringe the rights of others. You must not: (a) use the Service to store or transmit unlawful content; (b) attempt to gain unauthorized access to any part of the Service; (c) interfere with or disrupt the integrity or performance of the Service; or (d) reverse-engineer any portion of the Service.`,
  },
  {
    title: '5. Privacy & Data',
    body: `Our Privacy Policy describes how we collect, use, and share information about you when you use the Service. By using the Service, you agree to our collection and use of information in accordance with our Privacy Policy. You retain ownership of all data you submit to the Service. We process employee data solely on your behalf and in accordance with your instructions as the data controller.`,
  },
  {
    title: '6. Billing & Payments',
    body: `Subscription fees are billed in advance on a monthly or annual basis depending on your selected billing cycle. All fees are non-refundable except as required by law. We reserve the right to change subscription fees with 30 days' advance notice. Failure to pay may result in suspension or termination of your account. Trial periods are provided at our sole discretion and may be discontinued at any time.`,
  },
  {
    title: '7. Intellectual Property',
    body: `The Service and its original content, features, and functionality are and will remain the exclusive property of Flurry Systems and its licensors. You may not copy, modify, distribute, sell, or lease any part of the Service without our prior written consent.`,
  },
  {
    title: '8. Limitation of Liability',
    body: `To the maximum extent permitted by law, Flurry Systems shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising out of or in connection with your use of the Service. Our total liability to you for any claim arising under these Terms shall not exceed the amount paid by you for the Service in the three months preceding the claim.`,
  },
  {
    title: '9. Termination',
    body: `We may suspend or terminate your access to the Service immediately, without prior notice, if you breach these Terms or if we determine that continued access poses a risk to the Service or other users. Upon termination, your right to use the Service ceases immediately. Provisions that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.`,
  },
  {
    title: '10. Changes to Terms',
    body: `We reserve the right to modify these Terms at any time. When we make material changes, we will notify the account owner via email or an in-app notification. Continued use of the Service after changes become effective constitutes your acceptance of the revised Terms.`,
  },
  {
    title: '11. Governing Law',
    body: `These Terms shall be governed by the laws of the State of California, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and federal courts located in California.`,
  },
  {
    title: '12. Contact',
    body: `If you have any questions about these Terms, please contact us at support@clockearnings.com.`,
  },
];

export default function TermsModal({ isOpen, onAccept }) {
  const [checked, setChecked]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState('');

  const handleAccept = async () => {
    if (!checked || loading) return;
    setError('');
    setLoading(true);
    const result = await onAccept();
    if (!result?.success) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
    // On success the modal is unmounted by the parent — no need to reset state.
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position:       'fixed',
      inset:          0,
      zIndex:         99997,
      background:     'rgba(10,18,30,0.82)',
      backdropFilter: 'blur(4px)',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '24px 16px',
    }}>
      <div style={{
        background:    '#fff',
        borderRadius:  '18px',
        width:         '100%',
        maxWidth:      '660px',
        maxHeight:     '90vh',
        display:       'flex',
        flexDirection: 'column',
        boxShadow:     '0 32px 80px rgba(0,0,0,0.35)',
        overflow:      'hidden',
        fontFamily:    "'DM Sans','Segoe UI',sans-serif",
      }}>

        {/* Header */}
        <div style={{
          padding:      '1.5rem 2rem 1.25rem',
          borderBottom: '1px solid #e5e7eb',
          flexShrink:   0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: 'linear-gradient(135deg,#0f4d2e,#22c55e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>
              Terms &amp; Conditions
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: '0.825rem', color: '#6b7280', lineHeight: 1.5 }}>
            Please read and accept our Terms &amp; Conditions to continue using ClockEarnings.
            This is required before accessing the platform.
          </p>
        </div>

        {/* Scrollable content */}
        <div style={{
          flex:       1,
          overflowY:  'auto',
          padding:    '1.5rem 2rem',
        }}>
          <p style={{ margin: '0 0 1.25rem', fontSize: '0.825rem', color: '#6b7280' }}>
            Last updated: July 28, 2026
          </p>

          {TERMS_SECTIONS.map(({ title, body }) => (
            <div key={title} style={{ marginBottom: '1.25rem' }}>
              <h3 style={{
                margin: '0 0 0.4rem',
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#111827',
              }}>
                {title}
              </h3>
              <p style={{
                margin: 0,
                fontSize: '0.825rem',
                color: '#374151',
                lineHeight: 1.65,
              }}>
                {body}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding:      '1.25rem 2rem 1.5rem',
          borderTop:    '1px solid #e5e7eb',
          flexShrink:   0,
          background:   '#f9fafb',
        }}>
          {/* Checkbox */}
          <label style={{
            display:    'flex',
            alignItems: 'flex-start',
            gap:        '0.75rem',
            cursor:     'pointer',
            marginBottom: '1rem',
          }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              style={{
                width:      '18px',
                height:     '18px',
                marginTop:  '1px',
                cursor:     'pointer',
                accentColor: '#22c55e',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '0.85rem', color: '#374151', lineHeight: 1.5 }}>
              I have read and agree to the <strong>Terms &amp; Conditions</strong> on behalf of
              my organization. I understand that this agreement is legally binding.
            </span>
          </label>

          {error && (
            <p style={{
              margin:       '0 0 0.75rem',
              fontSize:     '0.8rem',
              color:        '#ef4444',
              background:   'rgba(239,68,68,0.08)',
              border:       '1px solid rgba(239,68,68,0.2)',
              borderRadius: '8px',
              padding:      '0.5rem 0.75rem',
            }}>
              {error}
            </p>
          )}

          <button
            onClick={handleAccept}
            disabled={!checked || loading}
            style={{
              width:         '100%',
              padding:       '0.75rem',
              background:    checked && !loading
                ? 'linear-gradient(135deg,#22c55e 0%,#0f4d2e 100%)'
                : '#d1d5db',
              color:         checked && !loading ? '#fff' : '#9ca3af',
              border:        'none',
              borderRadius:  '10px',
              fontSize:      '0.9rem',
              fontWeight:    700,
              cursor:        checked && !loading ? 'pointer' : 'not-allowed',
              transition:    'opacity 0.2s',
              letterSpacing: '0.01em',
              boxShadow:     checked && !loading ? '0 4px 16px rgba(34,197,94,0.3)' : 'none',
            }}
            onMouseEnter={e => { if (checked && !loading) e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            {loading ? 'Saving…' : 'Accept & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
